/**
 * Safe JSON Parsing Utility
 * 
 * This module provides safe response parsing to prevent the 
 * "Failed to execute 'json' on 'Response': body stream already read" error.
 * 
 * The error occurs when response.json() or response.text() is called 
 * more than once on the same Response object.
 */

/**
 * Safely parse a fetch Response as JSON
 * Reads the body ONCE and handles all edge cases
 * 
 * @param {Response} response - The fetch Response object
 * @returns {Promise<{ok: boolean, data: any, status: number, error?: string}>}
 */
export async function safeJson(response) {
  const status = response.status;
  const ok = response.ok;
  
  let text = '';
  let data = null;
  let cloned;
  
  try {
    // Clone response as a fallback in case .text() fails on some mobile browsers
    try { cloned = response.clone(); } catch (_) { /* clone not always available */ }

    // Read body ONCE as text
    text = await response.text();
    
    // Try to parse as JSON
    if (text && text.trim()) {
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        // Response was not valid JSON
        return {
          ok: false,
          status,
          data: null,
          error: 'Invalid JSON response',
          raw: text.substring(0, 500) // Include raw text for debugging
        };
      }
    } else {
      // Empty response body
      data = null;
    }
    
    return {
      ok,
      status,
      data,
      error: ok ? null : (data?.detail || data?.message || data?.error || 'Request failed')
    };
    
  } catch (readError) {
    // Body stream error - try fallback with cloned response
    const errMsg = (readError instanceof Error) ? readError.message : String(readError || 'Failed to read response');

    // Attempt recovery via cloned response (helps with mobile Safari body-stream issues)
    if (typeof cloned !== 'undefined' && cloned) {
      try {
        text = await cloned.text();
        if (text && text.trim()) {
          data = JSON.parse(text);
          return { ok, status, data, error: ok ? null : (data?.detail || data?.message || 'Request failed') };
        }
        return { ok, status, data: null, error: ok ? null : 'Empty response' };
      } catch (_fallbackErr) {
        // Both attempts failed
      }
    }

    console.warn('[safeJson] Body read failed:', errMsg, '(status:', status, ')');
    return {
      ok: false,
      status,
      data: null,
      error: errMsg
    };
  }
}

/**
 * Make a fetch request and safely parse the JSON response
 * 
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<{ok: boolean, data: any, status: number, error?: string}>}
 */
export async function safeFetch(url, options = {}) {
  try {
    const response = await fetch(url, options);
    return await safeJson(response);
  } catch (networkError) {
    console.error('[safeFetch] Network error:', networkError);
    return {
      ok: false,
      status: 0,
      data: null,
      error: networkError.message || 'Network error'
    };
  }
}

/**
 * Check handle availability with proper error handling and abort support
 * 
 * @param {string} handle - The handle to check
 * @param {string} apiBase - The API base URL
 * @param {AbortSignal} signal - Optional AbortController signal
 * @returns {Promise<{available: boolean, reason: string, handle: string, error?: string}>}
 */
export async function checkHandleAvailability(handle, apiBase, signal = null) {
  // Normalize handle
  const normalizedHandle = (handle || '')
    .trim()
    .toLowerCase()
    .replace(/^@/, '')
    .replace(/[^a-z0-9_]/g, '');
  
  // Quick validation
  if (!normalizedHandle || normalizedHandle.length < 3) {
    return {
      available: false,
      reason: 'Handle must be at least 3 characters',
      handle: normalizedHandle,
      error: 'too_short'
    };
  }
  
  if (normalizedHandle.length > 20) {
    return {
      available: false,
      reason: 'Handle must be 20 characters or less',
      handle: normalizedHandle,
      error: 'too_long'
    };
  }
  
  if (!/^[a-z][a-z0-9_]*$/.test(normalizedHandle)) {
    return {
      available: false,
      reason: 'Handle must start with a letter and contain only letters, numbers, and underscores',
      handle: normalizedHandle,
      error: 'invalid_format'
    };
  }
  
  try {
    const fetchOptions = {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    };
    
    if (signal) {
      fetchOptions.signal = signal;
    }
    
    const response = await fetch(
      `${apiBase}/handle/check?handle=${encodeURIComponent(normalizedHandle)}`,
      fetchOptions
    );
    
    const result = await safeJson(response);
    
    if (!result.ok || !result.data) {
      return {
        available: false,
        reason: result.error || "Can't verify handle right now - try again",
        handle: normalizedHandle,
        error: 'server_error'
      };
    }
    
    return {
      available: result.data.available === true,
      reason: result.data.reason || (result.data.available ? 'Handle is available' : 'Handle not available'),
      handle: result.data.handle || normalizedHandle
    };
    
  } catch (error) {
    // Handle abort errors silently
    if (error.name === 'AbortError') {
      return {
        available: false,
        reason: 'Request cancelled',
        handle: normalizedHandle,
        error: 'aborted'
      };
    }
    
    console.error('[checkHandleAvailability] Error:', error);
    return {
      available: false,
      reason: "Can't verify handle right now - try again",
      handle: normalizedHandle,
      error: 'network_error'
    };
  }
}

export default {
  safeJson,
  safeFetch,
  checkHandleAvailability
};
